/**
 * Cinema Mode - XML Message Processing
 *
 * This module handles all cinema mode related logic, including:
 * - XML to Markdown conversion
 * - Cinema message detection
 * - TTS text extraction for cinema messages
 * - User edit message patching for XML format
 */

/**
 * 检查字符是否是冒号（支持多种语言的冒号）
 */
const isColonChar = (char: string): boolean => {
  return [
    ':', // 英文冒号
    '：', // 中文/日文全角冒号 U+FF1A
    '꞉', // 修饰字母冒号 U+A789
    '∶', // 比例符号 U+2236
  ].includes(char)
}

/**
 * 查找冒号位置（支持多种语言的冒号）
 */
const findColonIndex = (text: string): number => {
  for (let i = 0; i < text.length; i++) {
    if (isColonChar(text[i])) {
      return i
    }
  }
  return -1
}

export type CinemaDisplaySegment = {
  displayText: string
  ttsText: string
}

/**
 * Cinema mode allowed XML tags for DOMPurify sanitization
 */
export const CINEMA_ALLOWED_TAGS = [
  'initial_script',
  'voiceover',
  'dialog',
  'image_prompt',
  'options',
  'option',
]

/**
 * Detects if a message is in cinema mode (XML format)
 */
export function isCinemaMessage(content: string): boolean {
  if (!content || typeof content !== 'string') return false
  return content.trim().startsWith('<initial_script>')
}

/**
 * Converts XML cinema message to Markdown format
 *
 * Rules:
 * - All XML tags are hidden (not displayed)
 * - <image_prompt> content is not displayed
 * - <options> content is not displayed
 * - <dialog> content: add quotes around the dialogue (after the colon)
 * - Each XML block is separated by one blank line
 *
 * @example
 * Input:
 * <initial_script>
 * <voiceover>The cafe door swings open...</voiceover>
 * <image_prompt>First-person POV...</image_prompt>
 * <dialog>Elaine: It's been five years...</dialog>
 * <voiceover>Something flickers...</voiceover>
 * <options>
 * <option>Apologize</option>
 * <option>Stay silent</option>
 * </options>
 * </initial_script>
 *
 * Output:
 * The cafe door swings open...
 *
 * Elaine: "It's been five years..."
 *
 * Something flickers...
 */
export function convertCinemaXmlToMarkdown(content: string): string {
  if (!isCinemaMessage(content)) {
    return content
  }

  try {
    const segments = extractCinemaDisplaySegments(content)
    if (segments.length === 0) return ''

    return segments.map((segment) => segment.displayText).join('\n\n')
  } catch (error) {
    console.error('Error converting cinema XML to Markdown:', error)
    return content
  }
}

const extractInitialScriptContent = (content: string): string | null => {
  const scriptMatch = content.match(
    /<initial_script>([\s\S]*?)<\/initial_script>/
  )
  return scriptMatch ? scriptMatch[1] : null
}

const pushPlainDisplaySegments = (
  segments: CinemaDisplaySegment[],
  content: string
) => {
  content
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .forEach((displayText) => segments.push({ displayText, ttsText: '' }))
}

const formatCinemaDialogDisplayText = (dialogContent: string): string => {
  const trimmed = dialogContent.trim()
  const colonIndex = findColonIndex(trimmed)
  if (colonIndex === -1) return trimmed

  const characterName = trimmed.substring(0, colonIndex)
  const dialogue = trimmed.substring(colonIndex + 1).trim()
  return `${characterName}: "${dialogue}"`
}

const extractCinemaDialogTtsText = (dialogContent: string): string => {
  const colonIndex = findColonIndex(dialogContent)
  if (colonIndex === -1) return ''

  return dialogContent.substring(colonIndex + 1).trim()
}

/**
 * Returns display blocks in cinema order. Only <dialog> blocks expose ttsText;
 * voiceover and plain blocks are kept for rendering but skipped by TTS.
 */
export function extractCinemaDisplaySegments(
  content: string
): CinemaDisplaySegment[] {
  if (!isCinemaMessage(content)) {
    return []
  }

  try {
    const scriptContent = extractInitialScriptContent(content)
    if (!scriptContent) return []

    const sanitizedScriptContent = scriptContent
      .replace(/<image_prompt>[\s\S]*?<\/image_prompt>/g, '')
      .replace(/<options>[\s\S]*?<\/options>/g, '')

    const segments: CinemaDisplaySegment[] = []
    const blockReg = /<(voiceover|dialog)>([\s\S]*?)<\/\1>/g
    let lastIndex = 0

    for (const match of sanitizedScriptContent.matchAll(blockReg)) {
      pushPlainDisplaySegments(
        segments,
        sanitizedScriptContent.slice(lastIndex, match.index)
      )

      const tagName = match[1]
      const blockContent = match[2].trim()
      if (blockContent) {
        if (tagName === 'dialog') {
          segments.push({
            displayText: formatCinemaDialogDisplayText(blockContent),
            ttsText: extractCinemaDialogTtsText(blockContent),
          })
        } else {
          pushPlainDisplaySegments(segments, blockContent)
        }
      }

      lastIndex = match.index + match[0].length
    }

    pushPlainDisplaySegments(segments, sanitizedScriptContent.slice(lastIndex))

    return segments
  } catch (error) {
    console.error('Error extracting cinema display segments:', error)
    return []
  }
}

/**
 * Extracts TTS (Text-to-Speech) text from cinema message
 *
 * For cinema mode:
 * - Extract only <dialog> content (without character names)
 * - Ignore <voiceover> content
 * - Ignore <image_prompt> and <options>
 *
 * @param content - Cinema XML message
 * @returns Extracted dialogue text for TTS
 */
export function extractCinemaTtsText(content: string): string {
  if (!isCinemaMessage(content)) {
    return ''
  }

  try {
    return extractCinemaDisplaySegments(content)
      .map((segment) => segment.ttsText)
      .filter((dialogue) => dialogue.length > 0)
      .join(' . ')
  } catch (error) {
    console.error('Error extracting cinema TTS text:', error)
    return ''
  }
}
